import { isNonEmpty, isNotDefined } from '@sgme/fp';
import classNames from 'classnames';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import type { FieldMetadata, SimpleGridFieldData, SimpleGridFieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';
import type { RowData } from './GridColumns';
import { GridColumns } from './GridColumns';
import { GridRow } from './GridRow';
import { Warning } from './Warning';

const EMPTY_ARRAY: FieldMetadata[] = [];
// used for obscure simple grid field like Additional settlement calendars in product mirabo on dynamic screen,
// until we know a little more about the business
export const SaferSimpleGridField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const [data, setData] = useData<SimpleGridFieldData>(id);

  const [isInsertionMode, setInsertionMode] = useState<boolean>(false);
  const hasError = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const metadata = useFieldMetadata<SimpleGridFieldMetadata>(id)!;
  const columnMetadata = metadata.columnFieldDefinitions ?? EMPTY_ARRAY;

  const columnFieldIds = columnMetadata?.map(m => m.id);

  const onAddRowClicked = useCallback(() => setInsertionMode(true), []);

  const rows = useMemo<ReadonlyArray<RowData>>(() => {
    if (isInsertionMode) {
      // empty row with null values
      const newEmptyRow = columnFieldIds?.reduce<RowData>((acc, fieldId) => {
        acc[fieldId] = null;
        return acc;
      }, {});
      if (isNotDefined(newEmptyRow)) {
        return data;
      }
      return [...data, newEmptyRow];
    } else {
      return data;
    }
  }, [data, isInsertionMode, columnFieldIds]);

  const onRowUpdated = useCallback(
    (rowIndex: number, value: RowData) => setData([...rows.slice(0, rowIndex), value, ...rows.slice(rowIndex + 1)]),
    [rows, setData],
  );

  const onRowRemoved = useCallback(
    (rowIndex: number) => {
      const updatedData = [...rows.slice(0, rowIndex), ...rows.slice(rowIndex + 1)];
      setData(updatedData);
    },
    [rows, setData],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Rerun this effect when data changes
  useEffect(() => setInsertionMode(false), [data]);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${metadata.translationKey}` as any}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <div className={classNames({ 'border border-warning': hasError })}>
        {rows.map((row, index) => (
          <GridRow key={index} fieldId={id} isLoading={false} index={index} removeRow={onRowRemoved}>
            <GridColumns fieldId={id} rowIndex={index} row={row} setRowValue={onRowUpdated} metadata={columnMetadata} />
          </GridRow>
        ))}
        <div className="row ms-0">
          <button
            type="button"
            id={id + '-add-line'}
            className="btn btn-flat-primary btn-icon-start text-start col-4"
            disabled={isInsertionMode}
            onClick={onAddRowClicked}
            data-e2e={id + '-item-add'}
            tabIndex={tabIndex}
          >
            <i className="icon">add</i>
            <FormattedMessage id="form.Add" />
          </button>
          {isNonEmpty(data) && <Warning warningId={metadata.infoTranslationKey as IntlKey} />}
        </div>
      </div>
    </BlockOrientationFormGroup>
  );
};

SaferSimpleGridField.displayName = 'SaferSimpleGridFieldNext';
